import { createStorage } from '@matix/utils';

/**
 * "Same neighbourhood, next customer" — the last address the cashier actually
 * typed is remembered on this terminal and pre-fills the next NEW address card
 * in the customer form.
 *
 * Only the *where* is carried over: country, city (المدينة), district/area
 * (الحي / المنطقة), avenue, block and street. Building number, additional
 * number, short address code, floor, flat and the note identify one specific
 * door — copying those onto the next customer would silently save someone
 * else's exact address, so they always start blank.
 *
 * Scoped per branch (a terminal that switches branch shouldn't suggest the
 * previous branch's streets) and expired after 24h, so yesterday's shift never
 * pre-fills today's.
 */

const STORE = createStorage('pos:cashier');
const KEY = 'last_address';
const TTL_MS = 24 * 60 * 60 * 1000;

export interface AddressSeed {
  country_id: number | null;
  governorate_id: number | null;
  area_id: number | null;
  avenue: string;
  block: string;
  street: string;
}

interface StoredSeed extends AddressSeed {
  saved_at: number;
}

function storageKey(branchId: number | null): string {
  return branchId == null ? KEY : `${KEY}:b${branchId}`;
}

/**
 * A country on its own is not worth remembering — every new address already
 * defaults to the tenant's country, so a seed carrying nothing else would only
 * light up the "prefilled" hint for no gain.
 */
function isUseful(seed: AddressSeed): boolean {
  return Boolean(
    seed.governorate_id ||
      seed.area_id ||
      seed.avenue.trim() ||
      seed.block.trim() ||
      seed.street.trim(),
  );
}

export function rememberAddressSeed(branchId: number | null, seed: AddressSeed): void {
  if (!isUseful(seed)) return;
  const stored: StoredSeed = {
    country_id: seed.country_id ?? null,
    governorate_id: seed.governorate_id ?? null,
    area_id: seed.area_id ?? null,
    avenue: seed.avenue.trim(),
    block: seed.block.trim(),
    street: seed.street.trim(),
    saved_at: Date.now(),
  };
  STORE.set(storageKey(branchId), stored);
}

export function readAddressSeed(branchId: number | null): AddressSeed | null {
  const raw = STORE.get<StoredSeed>(storageKey(branchId));
  if (!raw || typeof raw !== 'object') return null;
  if (typeof raw.saved_at !== 'number' || Date.now() - raw.saved_at > TTL_MS) return null;
  const seed: AddressSeed = {
    country_id: raw.country_id ?? null,
    governorate_id: raw.governorate_id ?? null,
    area_id: raw.area_id ?? null,
    avenue: typeof raw.avenue === 'string' ? raw.avenue : '',
    block: typeof raw.block === 'string' ? raw.block : '',
    street: typeof raw.street === 'string' ? raw.street : '',
  };
  return isUseful(seed) ? seed : null;
}
